import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

/**
 *
 */
@customElement('d-slide-divider')
export class DSlideDivider extends LitElement {
  static readonly styles = css`
    :host {
      display: flex;
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }
    #first {
      flex: none;
      display: flex;
      box-sizing: border-box;
      overflow: hidden;
    }
    #last {
      flex: 1;
      display: flex;
    }
    .divider-holder {
      flex: none;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 0;
      margin: 0 -1px;
      border-left: 2px dashed hsla(0, 0%, 0%, 0.2);
      opacity: 1;
      z-index: 2;
      transition: opacity 0.3s;
    }
    :host([vertical]) .divider-holder {
      margin: -1px 0;
      border-top: 2px dashed hsla(0, 0%, 0%, 0.2);
    }
    @media (hover: hover) {
      :host([hide-controls]) .divider-holder {
        opacity: 0;
      }
    }
    #divider {
      flex: none;
      display: flex;
      position: relative;
      left: -1px;
      box-sizing: border-box;
      width: 16px;
      height: 48px;
      border: 2px solid hsla(0, 0%, 0%, 0.2);
      border-radius: 13px;
      background: white;
      z-index: 2;
      cursor: col-resize;
      overflow: visible;
      pointer-events: all;
    }
    :host([vertical]) #divider {
      left: 0;
      top: -1px;
      width: 48px;
      height: 16px;
      cursor: row-resize;
    }
    @container (max-width: 600px) {
      #divider {
        width: 18px;
        height: 18px;
      }
    }
    :host([sub-divider]) .divider-holder {
      z-index: 1;
    }
    :host([sub-divider]) #divider {
      z-index: 1;
    }
    :host([vertical]) {
      flex-direction: column;
    }
    :host([vertical]) #first {
      width: 100%;
    }
    :host([vertical]) .divider-holder {
      flex-direction: column;
      width: 100%;
      height: 0;
    }
  `;
  @property({ type: Boolean, attribute: 'sub-divider', reflect: true })
  subDivider = false;
  @property({ type: Boolean })
  edit = false;
  @property({ type: Boolean, reflect: true })
  vertical = false;
  @property({ type: Number })
  value = 50;
  @property({ type: Number })
  min = 10;
  @property({ type: Number })
  max = 90;
  @query('#first')
  first!: HTMLDivElement;
  @query('#vdivider')
  divider!: HTMLDivElement;
  @state()
  containerSize = 0;
  @state()
  clientStart = 0;
  @state()
  firstElementSizeStart = 0;

  private firstStyle(size: number) {
    if (this.vertical) {
      return {
        height: size + '%',
      };
    }
    return {
      width: size + '%',
    };
  }

  private onMoveStart(clientX: number, clientY: number) {
    this.dispatchEvent(
      new CustomEvent('divider-move-start', {
        bubbles: true,
        composed: true,
      }),
    );
    if (this.vertical) {
      this.containerSize = this.getBoundingClientRect().height;
      this.clientStart = clientY;
      this.firstElementSizeStart = this.first.getBoundingClientRect().height;
    } else {
      this.containerSize = this.getBoundingClientRect().width;
      this.clientStart = clientX;
      this.firstElementSizeStart = this.first.getBoundingClientRect().width;
    }
  }

  private onMove(clientX: number, clientY: number) {
    if (this.vertical) {
      const distance = clientY - this.clientStart;
      const valuePixels = this.firstElementSizeStart + distance;
      const valuePercent = (valuePixels / this.containerSize) * 100;
      if (valuePercent > this.min && valuePercent < this.max) {
        this.first.style.height = valuePercent + '%';
      }
    } else {
      const distance = clientX - this.clientStart;
      const valuePixels = this.firstElementSizeStart + distance;
      const valuePercent = (valuePixels / this.containerSize) * 100;
      if (valuePercent > this.min && valuePercent < this.max) {
        this.first.style.width = valuePercent + '%';
      }
    }
  }

  private onMoveStop() {
    this.dispatchEvent(
      new CustomEvent('divider-move-stop', {
        bubbles: true,
        composed: true,
      }),
    );
    const firstRect = this.first.getBoundingClientRect();
    let valuePixels = firstRect.width;
    if (this.vertical) {
      valuePixels = firstRect.height;
    }
    this.value = (valuePixels / this.containerSize) * 100;
    this.dispatchEvent(
      new CustomEvent<{ value: number }>('value-changed', {
        bubbles: true,
        composed: true,
        detail: {
          value: this.value,
        },
      }),
    );
  }

  private onMouseMove(e: MouseEvent) {
    this.onMove(e.clientX, e.clientY);
  }

  onMouseStop() {
    this.onMoveStop();
    this.removeEventListener('mousemove', this.onMouseMove);
    this.removeEventListener('mouseup', this.onMouseStop);
  }

  private onMouseDown(e: MouseEvent) {
    this.onMoveStart(e.clientX, e.clientY);
    this.addEventListener('mousemove', this.onMouseMove);
    this.addEventListener('mouseup', this.onMouseStop);
  }

  private preventPageScroll(e: TouchEvent) {
    e.preventDefault();
  }

  private onTouchMove(e: TouchEvent) {
    this.onMove(e.touches[0].clientX, e.touches[0].clientY);
  }

  private onTouchStop() {
    this.onMoveStop();
    document.removeEventListener('touchmove', this.preventPageScroll);
    this.removeEventListener('touchmove', this.onTouchMove);
    this.removeEventListener('touchend', this.onTouchStop);
  }

  private onTouchStart(e: TouchEvent) {
    this.onMoveStart(e.touches[0].clientX, e.touches[0].clientY);
    document.addEventListener('touchmove', this.preventPageScroll, { passive: false });
    this.addEventListener('touchmove', this.onTouchMove);
    this.addEventListener('touchend', this.onTouchStop);
  }

  render() {
    return html`
      <div id="first" style=${styleMap(this.firstStyle(this.value))}>
        <slot name="first"></slot>
      </div>
      ${this.edit
        ? html`
            <div class="divider-holder">
              <div
                id="divider"
                @mousedown=${(e: MouseEvent) => this.onMouseDown(e)}
                @touchstart=${(e: TouchEvent) => this.onTouchStart(e)}
              ></div>
            </div>
          `
        : nothing}
      <div id="last" style="display: flex; flex-direction: row-reverse">
        <slot name="last"></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-slide-divider': DSlideDivider;
  }
}
